import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { generatePath } from 'react-router-dom'

import { Button, Tag, Tooltip } from 'antd'
import * as Icons from '@ant-design/icons'
import moment from 'moment'

import {
  AutoCompleteFormItem,
  FormItem,
  FormRow,
  Select
} from 'admin/lib/Forms'
import { Delivery as fields } from 'admin/lib/Records/fields'

import { Queries } from 'admin/lib/Api'
import { getCertStatus } from 'admin/lib/Certificates/CertificateExpiry'
import { Guid } from 'admin/lib/guid'
import * as ROUTES from 'admin/routes'

import { RAW } from 'admin/pages/RecordEdit/common/constants'

export const SupplierSelect = ({
  dataView = RAW,
  showRaw = false,
  isDisabled = (prop1, prop2, prop3) => false,
  isHidden = (prop1, prop2, prop3) => false
}) => {
  const { watch } = useFormContext()
  const selectedSupplierId = watch(fields.enriched.supplierId.path)

  const companies = Queries.useCompanies()
  const companyIsSelected =
    !!selectedSupplierId && selectedSupplierId !== Guid.Empty

  const companyEditPath = useMemo(
    () =>
      companyIsSelected
        ? generatePath(ROUTES.COMPANIES.EDIT, {
            companyId: selectedSupplierId
          })
        : '',
    [companyIsSelected, selectedSupplierId]
  )

  const handleCompaniesReload = () => {
    console.debug('Reloading companies...')
    companies.refetch()
  }

  const selectedCompany = useMemo(() => {
    if (!companyIsSelected || !companies.isFetched) {
      return null
    }

    return companies.data?.find(
      company => company.companyId === selectedSupplierId
    )
  }, [
    companies.data,
    companies.isFetched,
    companyIsSelected,
    selectedSupplierId
  ])

  const knownSupplierCertifications = useMemo(() => {
    if (
      !selectedCompany?.companyCertificates ||
      selectedCompany.companyCertificates.length === 0
    ) {
      return ['(None)']
    }

    return selectedCompany.companyCertificates.map(cert => ({
      value:
        cert.certificationScheme +
        (cert.certificateNumber ? ` (${cert.certificateNumber})` : ''),
      status: getCertStatus(cert),
      expirationDate: moment(cert.expirationDate).local().format('DD/MM/YYYY')
    }))
  }, [selectedCompany])

  return (
    <>
      <FormItem
        label={fields.raw.supplierName.label}
        name={fields.raw.supplierName.path}
        // Always show as disabled in Enriched
        disabled={isDisabled(
          fields.raw.supplierName.isRawField,
          dataView,
          true
        )}
        hidden={false}
        instrumented={true}
      />
      <AutoCompleteFormItem
        label={fields.enriched.supplierName.label}
        name={fields.enriched.supplierName.path}
        id="deliveryDataSupplierName"
        disabled={isDisabled(
          fields.enriched.supplierName.isRawField,
          dataView,
          showRaw
        )}
        hidden={true}
      />

      <FormRow>
        <FormItem
          label={fields.enriched.supplierId.label}
          name={fields.enriched.supplierId.path}
          altNames={[fields.enriched.knownSupplierName.path]}
          disabled={isDisabled(
            fields.enriched.supplierId.isRawField,
            dataView,
            showRaw
          )}
          hidden={isHidden(
            fields.enriched.supplierId.isRawField,
            dataView,
            showRaw
          )}
        >
          <Select
            loading={!companies.isFetched}
            showSearch
            status={!companyIsSelected ? 'warning' : undefined}
          >
            <Select.Option aria-label="(None)" value={Guid.Empty}>
              (None)
            </Select.Option>

            {companies.data?.map(company => (
              <Select.Option
                key={company.companyId}
                value={company.companyId}
                aria-label={company.name}
              >
                {company.name}
              </Select.Option>
            ))}
          </Select>
        </FormItem>
        {!isHidden(
          fields.enriched.supplierId.isRawField,
          dataView,
          showRaw
        ) && (
          <>
            <Tooltip title="Edit companies">
              <Button
                type="text"
                tabIndex="-1"
                icon={<Icons.EditFilled />}
                href={ROUTES.COMPANIES.LIST}
                target="_blank"
              />
            </Tooltip>
            <Tooltip title="Reload companies">
              <Button
                type="text"
                tabIndex="-1"
                icon={<Icons.ReloadOutlined />}
                onClick={handleCompaniesReload}
              />
            </Tooltip>
          </>
        )}
      </FormRow>
      <AutoCompleteFormItem
        label={fields.enriched.givenSupplierCertifications.label}
        name={fields.enriched.givenSupplierCertifications.path}
        disabled={isDisabled(
          fields.enriched.givenSupplierCertifications.isRawField,
          dataView,
          showRaw
        )}
        hidden={true}
      />

      {companyIsSelected && (
        <FormRow>
          <FormItem
            label={
              fields.enriched.knownSupplierCertifications.label + ' (readonly)'
            }
            name={fields.enriched.knownSupplierCertifications.path}
            disabled
            hidden={isHidden(
              fields.enriched.knownSupplierCertifications.isRawField,
              dataView,
              showRaw
            )}
          >
            <div>
              {knownSupplierCertifications?.map(cert => (
                <Tag
                  key={cert.value}
                  color={cert.status?.color}
                  title={
                    cert.status?.text &&
                    `${cert.status?.text}: ${cert.expirationDate}`
                  }
                >
                  {cert.value || ''}
                </Tag>
              ))}
            </div>
          </FormItem>
          {!isHidden(
            fields.enriched.knownSupplierCertifications.isRawField,
            dataView,
            showRaw
          ) && (
            <Button
              type="text"
              tabIndex="-1"
              icon={<Icons.EditFilled />}
              href={companyEditPath}
              target="_blank"
              style={{ alignSelf: 'center' }}
            />
          )}
        </FormRow>
      )}
    </>
  )
}
